/**
 * Anniversary decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const anniversaryDecorationsPack: ToilePack = {
    slug: 'anniversary-decorations',
    category: 'occasions',
    name: 'Anniversary decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bed', label: 'Rose bed', subject: 'a bed with rose petals in a heart'},
        {key: 'dinner', label: 'Candle dinner', subject: 'a table for two with candles'},
        {key: 'photos', label: 'Photo wall', subject: 'a wall of photos with fairy lights'},
    ],
    styles: [
        {key: 'simple', label: 'Simple budget', style: 'simple budget decoration with fresh rose petals, a few balloons and paper hearts, soft daylight from a window camera-left, a bright airy balance with gentle shadows', palette: 'red and white with pale pink touches', framing: 'eye-level view, the whole setup in frame with the room around it'},
        {key: 'candlelit', label: 'Candlelight', style: 'evening decoration with tea-light candles and warm string lights, the glow falling from camera-right, a dim warm balance with deep soft shadows', palette: 'deep red, gold and warm amber', framing: 'close three-quarter view, the setup filling the frame, the room fading into darkness'},
        {key: 'luxury', label: 'Luxury', style: 'lavish decoration with dense rose garlands, sheer white drapes and gold accents, even soft light from overhead, a rich balance of bright highlights and gentle shadow', palette: 'ivory, blush pink and gold', framing: 'wide straight-on view, symmetric, the decoration centered'},
    ],
    directions: ['golden jubilee', 'balcony', 'white flowers'],
    directionHint: 'try: golden jubilee',
    seo: {
        title: 'Anniversary decoration at home: bed, dinner, photo wall',
        metaDescription: 'Nine anniversary decoration pictures for home: a rose petal bed, a candle dinner and a photo wall, in simple, candlelit and luxury looks.',
        h1: 'Anniversary decoration at home: rose bed, candle dinner, photo wall',
        tagline: 'Nine pictures, three anniversary setups, each in a simple budget, a candlelit and a luxury look.',
        intro: 'Anniversary decoration at home for a couple: a bed with rose petals in a heart, a table for two with candles, and a wall of photos with fairy lights. The simple look uses fresh petals, a few balloons and paper hearts in daylight. The candlelit look has tea-light candles and warm string lights. The luxury look has dense rose garlands and sheer white drapes. Type a twist, such as golden jubilee or white flowers, and the same setups carry it.',
        useCases: [
            'an anniversary surprise in a bedroom',
            'a dinner table for two at home',
            'a photo wall of family memories',
            'a simple or a luxury setup, by budget',
        ],
        faq: [
            {q: 'What makes the simple look simple?', a: 'Fresh rose petals, a few balloons and paper hearts, shown in soft daylight from a window.'},
            {q: 'Are the candlelit pictures lit by candles?', a: 'Yes. They show tea-light candles and warm string lights in a dim room.'},
            {q: 'Can I ask for a golden jubilee?', a: 'Yes. Type a twist such as golden jubilee or white flowers, and the same three setups carry it.'},
            {q: 'Are these real photographs?', a: 'No. They are generated pictures made to look like photographs of a room, with no people and no text.'},
        ],
    },
}
